/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomTextFieldN.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Box, TextField } from "@mui/material";
import React from "react";
import PropTypes from "prop-types";

const CustomTextFieldN = ({
  size,
  label,
  type,
  fldType,
  placeholder,
  required,
  value,
  setValue,
  variant,
  valueError,
  setValueError = () => null,
  inputProps,
  disabled,
  isMultiline = false,
  minRows = 1,
  maxRows = 3,
  txtFldStyle,
}) => {
  const regexMin3 = /^(?=.*[a-zA-Z0-9._-])[a-zA-Z0-9._-]{3,}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateMinLength = (val, min) => {
    const str = typeof val === "string" ? val : String(val || "");
    return str.trim().length < min ? `Minimum ${min} characters required` : "";
  };

  const handleTextR = (val) => {
    const str = typeof val === "string" ? val : String(val || "");
    const noSpaceValue = str.replaceAll(/\s+/g, "");

    if (noSpaceValue.trim().length < 3) {
      return "Minimum 3 characters required";
    }

    if (!regexMin3.test(noSpaceValue)) {
      return "Invalid format";
    }

    return "";
  };

  const handleTextO = (val) => {
    const str = typeof val === "string" ? val : String(val || "");
    const len = str.trim().length;

    if (len > 0 && len < 3) {
      return "Minimum 3 characters required";
    }

    return "";
  };

  const handleEmail = (val) => {
    const str = typeof val === "string" ? val : String(val || "");
    return emailRegex.test(str) ? "" : "Invalid email address";
  };

  const handleChange = (e) => {
    let newValue = e.target.value;

    newValue = newValue.replace(/^\s+/, "");
    newValue = newValue.replace(/\s+$/, " ");

    let error = "";

    switch (fldType) {
      case "text1":
        error = validateMinLength(newValue, 1);
        break;

      case "text":
        error = validateMinLength(newValue, 3);
        break;

      case "textR":
        error = handleTextR(newValue);
        newValue = newValue.replaceAll(/\s+/g, ""); // keep original behavior
        break;

      case "textO":
        error = handleTextO(newValue);
        break;

      case "text4":
        error = validateMinLength(newValue, 4);
        break;

      case "text5":
        error = validateMinLength(newValue, 5);
        break;

      case "email":
        error = handleEmail(newValue);
        break;

      default:
        break;
    }

    if (setValueError) {
      setValueError(error);
    }
    if (setValue) {
      setValue(newValue);
    }
  };

  return (
    <Box>
      <TextField
        fullWidth
        size={size}
        label={label}
        type={type}
        required={required}
        error={Boolean(valueError)}
        helperText={valueError}
        variant={variant}
        placeholder={placeholder}
        value={value}
        InputProps={inputProps}
        onChange={handleChange}
        multiline={isMultiline}
        minRows={minRows}
        maxRows={maxRows}
        sx={txtFldStyle}
        disabled={disabled}
      />
    </Box>
  );
};

CustomTextFieldN.propTypes = {
  size: PropTypes.string,
  label: PropTypes.node,
  type: PropTypes.string,
  fldType: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setValue: PropTypes.func,
  variant: PropTypes.string,
  valueError: PropTypes.string,
  setValueError: PropTypes.func,
  inputProps: PropTypes.object,
  disabled: PropTypes.bool,
  isMultiline: PropTypes.bool,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  txtFldStyle: PropTypes.object,
};

export default CustomTextFieldN;
